<template>
    <section class="student-edit-view">
        <div class="modal-header">
            <h3 v-if="student.uuid">Student Edit</h3>
            <h3 v-else>Add Student</h3>
            <p class="required-fields">All fields marked with * are required</p>
        </div>
        <app-alert v-if="student.deleted_at">
            This student has been archived. To make them active again, please hit the button below.
            <div class="alert-buttons">
                <app-button type="primary" @click="restore">Restore</app-button>
            </div>
        </app-alert>
        <app-form
            ref="studentForm"
            :model="student"
            :rules="rules"
            @submit.native.prevent="submit">
            <grid :columns="['1fr']">
                <grid-item>
                    <app-form-item label="First Name" prop="first_name">
                        <app-input id="first_name" v-model="student.first_name" autofocus/>
                    </app-form-item>
                </grid-item>
                <grid-item>
                    <app-form-item label="Last Name" prop="last_name">
                        <app-input id="last_name" v-model="student.last_name"/>
                    </app-form-item>
                </grid-item>
                <grid-item>
                    <app-form-item label="Email" prop="email">
                        <app-input id="email" v-model="student.email"/>
                    </app-form-item>
                </grid-item>
                <grid-item>
                    <app-form-item label="Phone" prop="phone">
                        <app-input id="phone" v-model="student.phone"/>
                    </app-form-item>
                </grid-item>
                <grid-item>
                    <app-form-item label="School Identifier" prop="school_identifier">
                        <app-input id="school_identifier" v-model="student.school_identifier"/>
                    </app-form-item>
                </grid-item>
                <grid-item>
                    <app-form-item label="Is At Risk" prop="at_risk">
                        <app-radio-group v-model="student.at_risk" size="medium">
                            <app-radio :label="true" border>Yes</app-radio>
                            <app-radio :label="false" border>No</app-radio>
                        </app-radio-group>
                    </app-form-item>
                </grid-item>
            </grid>

            <div class="modal-footer">
                <app-button
                    :loading="isSaving"
                    type="primary"
                    native-type="submit"
                    size="large">Save</app-button>

                <template v-if="student.uuid && role <= 5">
                    <app-button
                        v-if="!student.deleted_at"
                        :border="false"
                        type="danger"
                        size="large"
                        tabindex="1"
                        style="float:left"
                        plain
                        @click="archive">Archive</app-button>
                    <app-button
                        v-else
                        :border="false"
                        type="danger"
                        size="large"
                        tabindex="1"
                        style="float:left"
                        plain
                        @click="remove">Delete</app-button>
                </template>
            </div>
        </app-form>
    </section>
</template>
<script>
import { mapGetters } from 'vuex'
import Student from '@/store/models/Student'

export default {
    name: 'student-edit-view',
    inject: ['modalOptions'],
    props: ['id'],
    data() {
        return {
            student: Student.$cloneOrNew(this.id),
            isSaving: false,
            rules: {
                first_name: [
                    {
                        required: true,
                        message: 'Please enter a first name',
                        trigger: 'blur'
                    }
                ],
                last_name: [
                    {
                        required: true,
                        message: 'Please enter a last name',
                        trigger: 'blur'
                    }
                ],
                email: [
                    {
                        required: true,
                        message: 'Please enter an email',
                        trigger: 'blur'
                    }
                ]
            }
        }
    },
    computed: {
        ...mapGetters(['role'])
    },
    methods: {
        submit() {
            this.$refs['studentForm'].validate(valid => {
                if (valid) {
                    this.student.uuid ? this.update() : this.create()
                }
            })
        },
        create() {
            this.isSaving = true
            this.student.$create()
                .then(({ data }) => {
                    this.isSaving = false
                    this.modalOptions.modalOpen = false
                    this.$router.push({
                        name: 'students.detail',
                        params: { uuid: data.uuid }
                    })
                })
                .catch(error => {
                    this.isSaving = false
                    if (error.code == 1010) {
                        this.redirect(error.message, error.model)
                    }
                })

            return false
        },
        update() {
            this.isSaving = true
            this.student.$update()
                .then(() => {
                    this.isSaving = false
                    this.modalOptions.modalOpen = false
                })
                .catch(error => {
                    this.isSaving = false
                    if (error.code == 1010) {
                        this.redirect(error.message, error.model)
                    }
                })

            return false
        },
        restore() {
            Student.$mutate({
                graphql: 'restore',
                variables: { id: this.id }
            })
                .then(() => {
                    this.modalOptions.modalOpen = false
                })
        },
        archive() {
            this.$confirm({
                title: 'Confirm',
                message:
                    'You are about to archive a student. No data will be lost, they will just be inactive and hidden.'
            })
                .then(() => {
                    return this.student.$remove()
                })
                .then(() => {
                    const prevPage =
                        localStorage.getItem('prevPage') || '/Students'
                    this.$router.push({ path: prevPage })
                })
        },
        remove() {
            this.$confirm({
                title: 'Confirm',
                message:
                    'Are you sure you want to remove this student? All of their data will be removed.'
            })
                .then(() => {
                    return this.student.$remove()
                })
                .then(() => {
                    const prevPage =
                        localStorage.getItem('prevPage') || '/Students'
                    this.$router.push({ path: prevPage })
                })
        },
        redirect(message, student) {
            if (confirm(message)) {
                this.$router.push({
                    name: 'students.detail',
                    params: { uuid: student.uuid }
                })
            }
        }
    }
}
</script>

<style lang="stylus"></style>
